import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ConsultationCTA from '../../components/ui/ConsultationCTA';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ServiceCard from './components/ServiceCard';
import ComparisonMatrix from './components/ComparisonMatrix';
import ConsultationForm from './components/ConsultationForm';
import ServiceTestimonials from './components/ServiceTestimonials';

const ServicesOverview = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const services = [
    {
      id: 'software-development',
      title: 'Software Development',
      icon: 'Code',
      description: 'Custom software solutions built with cutting-edge technologies and best practices. From web applications to enterprise systems, we deliver scalable, maintainable code that drives business growth.',
      benefits: [
        'Full-stack development expertise',
        'Agile development methodology',
        'Cloud-native architecture',
        'Continuous integration & deployment',
        'Performance optimization'
      ],
      metrics: [
        { value: '99.9%', label: 'Uptime' },
        { value: '50%', label: 'Faster TTM' }
      ],
      category: 'development'
    },
    {
      id: 'ai-models',
      title: 'AI Models & Machine Learning',
      icon: 'Brain',
      description: 'Advanced AI and machine learning solutions tailored to your business needs. We develop custom models for predictive analytics, natural language processing, computer vision, and more.',
      benefits: [
        'Custom model development',
        'Pre-trained model fine-tuning',
        'MLOps pipeline implementation',
        'Model monitoring & maintenance',
        'Ethical AI practices'
      ],
      metrics: [
        { value: '95%', label: 'Accuracy' },
        { value: '60%', label: 'Cost Reduction' }
      ],
      category: 'ai'
    },
    {
      id: 'analytics',
      title: 'Data Analytics & Intelligence',
      icon: 'BarChart3',
      description: 'Transform your data into actionable insights with our comprehensive analytics solutions. From data warehousing to real-time dashboards, we help you make data-driven decisions.',
      benefits: [
        'Real-time data processing',
        'Interactive dashboards',
        'Predictive analytics',
        'Data visualization',
        'Business intelligence reporting'
      ],
      metrics: [
        { value: '10x', label: 'Faster Insights' },
        { value: '40%', label: 'Better Decisions' }
      ],
      category: 'analytics'
    },
    {
      id: 'cybersecurity',
      title: 'Cybersecurity Solutions',
      icon: 'Shield',
      description: 'Comprehensive cybersecurity services to protect your digital assets. From threat assessment to incident response, we provide multi-layered security solutions.',
      benefits: [
        'Threat detection & prevention',
        'Security audits & assessments',
        'Compliance management',
        'Incident response planning',
        'Employee security training'
      ],
      metrics: [
        { value: '99.8%', label: 'Threat Detection' },
        { value: '24/7', label: 'Monitoring' }
      ],
      category: 'security'
    },
    {
      id: 'ai-engineering',
      title: 'AI Engineering & Integration',
      icon: 'Cpu',
      description: 'Seamlessly integrate AI capabilities into your existing systems. Our AI engineering services ensure smooth deployment, scaling, and maintenance of AI solutions.',
      benefits: [
        'AI system architecture',
        'Legacy system integration',
        'Scalable AI infrastructure',
        'Performance optimization',
        'Continuous model improvement'
      ],
      metrics: [
        { value: '3x', label: 'Performance' },
        { value: '80%', label: 'Automation' }
      ],
      category: 'ai'
    }
  ];

  const categories = [
    { id: 'all', label: 'All Services', icon: 'Grid3X3' },
    { id: 'development', label: 'Development', icon: 'Code' },
    { id: 'ai', label: 'AI & ML', icon: 'Brain' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'security', label: 'Security', icon: 'Shield' }
  ];

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(service => service.category === selectedCategory);

  const handleLearnMore = (service) => {
    navigate('/individual-service-detail', { 
      state: { 
        serviceId: service.id,
        serviceTitle: service.title 
      } 
    });
  };

  return (
    <div className="min-h-screen bg-muted">
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Breadcrumb />
        
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Sparkles" size={16} />
            <span>Comprehensive AI & Technology Solutions</span>
          </div>
          
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Transform Your Business with
            <span className="block gradient-primary bg-clip-text text-transparent">
              Expert Consultancy Services
            </span>
          </h1>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
            From custom software development to advanced AI implementation, our comprehensive suite of services helps organizations leverage cutting-edge technology to achieve their strategic objectives and drive sustainable growth.
          </p>
          
          <div className="flex items-center justify-center space-x-4">
            <Button
              variant="default"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => navigate('/contact-consultation-request')}
            >
              Schedule Consultation
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Play"
              iconPosition="left"
            >
              Watch Demo
            </Button>
          </div>
        </div>

        {/* Service Categories Filter */}
        <div className="flex items-center justify-center mb-8">
          <div className="bg-white rounded-xl border border-border p-2 flex items-center space-x-2 overflow-x-auto">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={category.icon} size={16} />
                <span>{category.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-3 space-y-8">
            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  onLearnMore={handleLearnMore}
                />
              ))}
            </div>

            {/* Comparison Matrix */}
            <ComparisonMatrix services={services} />

            {/* Service Testimonials */}
            <ServiceTestimonials services={services} />

            {/* Inline CTA */}
            <ConsultationCTA variant="inline" context="services" />
          </div>

          {/* Sidebar */}
          <div className="xl:col-span-1">
            <ConsultationForm services={services} />
          </div>
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-16">
          <div className="gradient-primary rounded-xl p-8 text-center text-white">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold mb-4">
                Ready to Start Your Digital Transformation?
              </h2>
              <p className="text-white/90 mb-6 text-lg">
                Join 200+ companies that have successfully implemented AI and technology solutions with RCEG AI Ventures.
              </p>
              <div className="flex items-center justify-center space-x-4">
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => navigate('/about-us')}
                >
                  Learn About Us
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => navigate('/contact-consultation-request')}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Get Started Today
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Floating CTA */}
      <ConsultationCTA variant="floating" context="services" />
    </div>
  );
};

export default ServicesOverview;